// AssistantPill — the minimised chat panel (pen K-08 "Assistant minimised –
// working in the background"): one card docked bottom-right that says what
// the assistant is doing and opens the panel again.
//
// Pure: renders an `AssistantPillState` (projected by app/assistantPill.ts)
// and maps a click to `onOpen`. The whole card is ONE button (click, Enter or
// Space opens); its text is a polite live region, so a new step or count is
// announced once — the stream's tokens never reach it.
//
// `done` shows "Done" and then settles into the idle look by a CSS delay
// (AssistantPill.css), not a timer: the state itself never changes for it.
//
// `compact` is the Studio shell's pill (pen "Projects and Studio", decision 1:
// "Assistant" + Open, 44 high, bottom right): one line, no detail.

export { AssistantPill };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { interruptionCopy } from "../Chat/ChatMessage/interruptionCopy.js";
import { liveActivityLabel } from "../Chat/toolStepLabels.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import "../Spinner/Spinner.css";
import "./AssistantPill.css";
import type { AssistantPillState } from "./types.js";

type Copy = { readonly title: string; readonly detail?: string };

const Glyph = ({ name }: { readonly name: IconName | "busy" }): ReactNode =>
    name === "busy" ? (
        <span className="bilda-spinner bilda-spinner--sm" aria-hidden="true" />
    ) : (
        <Icon name={name} size="sm" />
    );

const AssistantPill = ({
    state,
    compact = false,
    onOpen,
}: {
    readonly state: AssistantPillState;
    readonly compact?: boolean;
    readonly onOpen: () => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const idle: Copy = { title: t`Assistant` };

    const [glyph, copy]: readonly [IconName | "busy", Copy] = (() => {
        switch (state.type) {
            case "working": {
                const title =
                    state.step.type === "live"
                        ? liveActivityLabel(state.step.activity, (m) =>
                              i18n._(m),
                          )
                        : t`Sending your message`;
                const { progress } = state;
                const detail =
                    progress === undefined
                        ? t`You can keep working`
                        : t`${progress.done} of ${progress.total} done · you can keep working`;
                return ["busy", { title, detail }];
            }
            case "needs_you":
                return [
                    "help",
                    {
                        title: t`Your answer is needed`,
                        detail: t`The assistant asked you something`,
                    },
                ];
            case "interrupted":
                return [
                    "info",
                    {
                        title: i18n._(interruptionCopy(state.cause).happened),
                        detail: t`Open to see what happened`,
                    },
                ];
            case "done":
                return ["check", { title: t`Done` }];
            case "idle":
                return ["sparkle", idle];
            default:
                return exhaustSwitchCase(state);
        }
    })();

    return (
        <button
            type="button"
            className="bilda-assistant-pill"
            data-state={state.type}
            data-compact={compact ? true : undefined}
            onClick={onOpen}
        >
            <span className="bilda-assistant-pill__mark">
                <span className="bilda-assistant-pill__layer">
                    <Glyph name={glyph} />
                </span>
                {state.type === "done" ? (
                    <span className="bilda-assistant-pill__layer bilda-assistant-pill__layer--after">
                        <Glyph name="sparkle" />
                    </span>
                ) : null}
            </span>
            <span className="bilda-assistant-pill__text" aria-live="polite">
                <span className="bilda-assistant-pill__layer">
                    <span className="bilda-assistant-pill__title">
                        {copy.title}
                    </span>
                    {copy.detail === undefined || compact ? null : (
                        <span className="bilda-assistant-pill__detail">
                            {copy.detail}
                        </span>
                    )}
                </span>
                {state.type === "done" ? (
                    <span
                        className="bilda-assistant-pill__layer bilda-assistant-pill__layer--after"
                        aria-hidden="true"
                    >
                        <span className="bilda-assistant-pill__title">
                            {idle.title}
                        </span>
                    </span>
                ) : null}
            </span>
            <span className="bilda-assistant-pill__open">
                {t`Open`}
                {compact ? null : <Icon name="chevrons-down" size="sm" />}
            </span>
        </button>
    );
};
